import classNames from 'classnames'
import { Input } from 'components/atom/Input'
import { TextArea, TextAreaContent } from 'components/atom/TextArea'
import { CircularCheckbox } from 'components/CircularCheckbox'
import { ShadowBox } from 'components/ShadowBox'
import { TextTypeAiScriptDisplay } from 'pages/content-video-editor/components/shortvideo/TextTypeAiScriptDisplay'
import useGetLanguages from 'hooks/useGetLanguages'
import useGetSourceType from 'hooks/useGetSourceType'
import useGetVoiceLanguageGroup from 'hooks/useGetVoiceLanguageGroup'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { SourceType, TextTypeVideoInfo, VoiceTagType } from 'service/list'
import useUserStore from 'store/useUserStore'
import { getLocalStorage, saveLocalStorage, styles } from 'utils/common'
import { contentVideoTextTypeFormValues } from 'utils/type'
import TextCashUseModal from './TextCashUseModal'
import { useGetMyPoint } from 'hooks/query/point/useGetMyPoint'
import { Modal } from 'components/layout/Modal'

import { VideoRatioType } from '../shortvideo/VideoRatioType'
import { LocalImageUpload } from '../ImageUploadBox/local-image-upload'
import { ReactComponent as TrashIcon } from 'svg/fi_trash-2.svg'
import { useUploadFile } from 'hooks/query/s3/useUploadFile'
import { toast } from 'react-toastify'
import useDeleteFile from 'hooks/query/s3/useDeleteFile'
import { useTranslation } from 'react-i18next'
import {
  isInvalidFileExtension,
  setDefaultValueFromListInTextvideo,
  splitTextIntoScenes,
} from 'pages/content-video-editor/utils'
import { SubtitleDisplay } from './SubtitleDisplay'
import { useGetSubtitleShort } from 'hooks/query/useGetSubtitle'
import useGetSubtitleColor from 'hooks/query/useGetSubtitleColor'
import useGetUserVoice from 'hooks/query/useGetUserVoice'

interface TextTypeProps {
  tab: 'link' | 'text'
  setTab: Dispatch<SetStateAction<'link' | 'text'>>
  changeTab: (string: 'link' | 'text') => void
  voiceTags: VoiceTagType | undefined
}

export const TextType = ({
  tab,
  setTab,
  changeTab,
  voiceTags,
}: PropsWithChildren<TextTypeProps>) => {
  const { t } = useTranslation()
  const { mutateAsync: uploadFileMutate, isPending } = useUploadFile()
  const { mutateAsync: deleteMutate } = useDeleteFile()

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<contentVideoTextTypeFormValues>({
    defaultValues: {
      idea: '',
      title: '',
      userWritten: 0,
      sourceType: '',
      languageId: 0,
      voiceId: 0,
      userVoiceId: 0,
      sourceTypeId: 0,
      amount: 0,
      withShort: 0,
      subtitleId: 0,
      subtitleColorId: 0,
    },
  })

  const [textVideoInfo, setTextVideoInfo] = useState<TextTypeVideoInfo>({
    idea: '',
    title: '',
    userWritten: 0,
    voiceId: 0,
    userVoiceId: 0,
    sourceType: '',
    sourceTypeId: 0,
    languageId: 0,
    amount: 0,
    subtitleColorId: 0,
    subtitleId: 0,
    withShort: 0,
    urls: [],
  })

  const PRODUCT_NAME_PATTERN = /^[^<>\uD800-\uDFFF]*$/
  const userInfo = useUserStore((state: any) => state.userInfo)
  const navigate = useNavigate()
  const [textVideoPriceInfo, setTextVideoPriceInfo] = useState<{
    voicePrice: number
    sourceTypePrice?: number
    subtitlePrice: number
  }>({
    voicePrice: 0,
    subtitlePrice: 0,
  })
  const [subtitleTooltip, setSubtitleTooltip] = useState<boolean>(false)
  const [saveSuccessModal, setSaveSuccessModal] = useState<boolean>(false)
  const [isUseModal, setUseModal] = useState<boolean>(false)
  const [isErrorModal, setIsErrorModal] = useState<boolean>(false)
  const [isSceneErrorModal, setIsSceneErrorModal] = useState<boolean>(false)
  const [errorTitle, setErrorTitle] = useState(<></>)
  const [errorSubtitle, setErrorSubtitle] = useState(<></>)

  const [selectedSourceType, setSelectedSourceType] =
    useState<SourceType | null>(null)
  const [selectedSourceTypeChildrenId, setSelectedSourceTypeChildrenId] =
    useState<number>(0)
  const [uploadImages, setUploadImages] = useState<
    {
      id: number
      file: File
      preview: string
      uploading: boolean
    }[]
  >([])

  const [selectedUploadImages, setSelectedUploadImages] = useState(
    new Array(uploadImages.length).fill(true)
  )

  const [userWritten, setUserWritten] = useState<boolean>(false)
  const [maxLength, setMaxLength] = useState<number>(1000)

  // 데이터
  const { data: sourceTypeList } = useGetSourceType()
  const { data: languageList } = useGetLanguages()
  const { data: myPointData, refetch: getMyPointRefetch } = useGetMyPoint()
  const { data: voiceList, refetch: voiceRefetch } = useGetVoiceLanguageGroup(
    watch('languageId')
  )
  const { data: userVoiceList, refetch: userVoiceRefetch } = useGetUserVoice()

  const { data: subTitleList } = useGetSubtitleShort()
  const { data: subtitleColorList } = useGetSubtitleColor()

  const userCheck = (userInfo: any) => {
    if (!userInfo) {
      navigate('/sign-up')
      return
    }
  }
  const handleInitData = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setUploadImages([])

    setValue('idea', '')
    setValue('title', '')
  }
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files) return

    const MAX_FILE_SIZE_MB = 5
    const selectedFiles = Array.from(event.target.files)
    const originalFile = selectedFiles[0]

    const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']
    if (isInvalidFileExtension(originalFile.name, ALLOWED_EXTENSIONS)) {
      toast.error(
        `❌ ${originalFile.name}${t(
          'contentVideoEditor.textType.errors.invalidFileType'
        )}`,
        {
          position: 'bottom-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        }
      )
      return
    }

    const fileSizeMB = originalFile.size / (1024 * 1024)
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error(
        `❌ ${originalFile.name}${t(
          'contentVideoEditor.textType.errors.fileSizeExceeded',
          { size: MAX_FILE_SIZE_MB }
        )}`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        }
      )
      return
    }

    const tempId = Date.now() + Math.random()
    const tempPreview = URL.createObjectURL(originalFile)
    setUploadImages((prev) => [
      ...prev,
      {
        file: originalFile,
        preview: tempPreview,
        uploading: true,
        id: tempId,
      },
    ])

    try {
      const { fileUrl } = await uploadFileMutate(originalFile)
      setUploadImages((prev) =>
        prev.map((item) =>
          item.id === tempId
            ? {
                ...item,
                preview: fileUrl,
                uploading: false,
              }
            : item
        )
      )
    } catch (error) {
      toast.error(
        `❌ ${originalFile.name}${t(
          'contentVideoEditor.textType.errors.uploadFailed'
        )}`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        }
      )
      console.error(error)
    } finally {
      URL.revokeObjectURL(tempPreview)
      event.target.value = ''
    }
  }
  const getFileKey = (file: File) =>
    `${file.name}_${file.size}_${file.lastModified}`
  const handleDeleteFile = async (targetFile: File, targetUrl: string) => {
    const targetKey = getFileKey(targetFile)

    setUploadImages((prev) =>
      prev.filter((imgObj) => imgObj.preview !== targetUrl)
    )
    await deleteMutate(targetUrl)
    setSelectedUploadImages((prev) => {
      const targetIndex = uploadImages.findIndex(
        (imgObj) => getFileKey(imgObj.file) === targetKey
      )
      if (targetIndex === -1) return prev
      const newSelected = [...prev]
      newSelected.splice(targetIndex, 1)
      return newSelected
    })
  }

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    document.getElementById('file-input')!.click()
  }

  const onSubmit = (data: any) => {
    console.log(data)
    userCheck(userInfo)
    // localstorage에 데이터 저장
    let {
      idea,
      title,
      userWritten,
      sourceType,
      sourceTypeId,
      languageId,
      voiceId,
      userVoiceId,
      withShort,
      subtitleId,
      subtitleColorId,
    } = data

    const prevInfo = {
      idea,
      title,
      sourceType,
      sourceTypeId,
      languageId,
      voiceId,
      userVoiceId,
      subtitleId,
      subtitleColorId,
      withShort,
    }
    //! 제일 첫번째 내가쓴 대본입력했을 경우 4문단이 나오는지 확인 필요
    if (userWritten) {
      const scenes = splitTextIntoScenes(idea, 4)
      const scenesLength = scenes.length
      console.log(scenes, scenesLength)
      if (scenesLength < 4) return setIsSceneErrorModal(true)
    }

    saveLocalStorage('contentTextVideoData', prevInfo)

    //sourceType이 이미지 업로드, sourceTypeId가 3일때 사진4장 확인

    if (sourceTypeId === 3 && uploadImages.length !== 4) {
      setErrorTitle(
        <p className="font-bold text-[18px]">
          {t('contentVideoEditor.textType.modals.imageError.title')}
        </p>
      )
      setErrorSubtitle(
        <p className="text-[14px] ">
          {t('contentVideoEditor.textType.modals.imageError.description')}
        </p>
      )
      setIsErrorModal(true)
      return
    } else if (Number(voiceId) === 0 && Number(userVoiceId) === 0) {
      setErrorTitle(
        <p className="font-bold text-[18px]">
          {t('contentVideoEditor.textType.modals.voiceError.title')}
        </p>
      )
      setErrorSubtitle(
        <p className="text-[14px] ">
          {t('contentVideoEditor.textType.modals.voiceError.description')}
        </p>
      )
      setIsErrorModal(true)
      return
    }

    // 파일 확인
    const selectedUploadImageData = uploadImages.map((el) => el.preview)
    const urls = [...(selectedUploadImageData ?? [])]
    const files = selectedUploadImageData || []
    const textVideoData = {
      idea,
      subtitleId,
      subtitleColorId,
      title,
      userWritten,
      sourceType,
      languageId,
      sourceTypeId,
      voiceId,
      userVoiceId,
      amount: 0,
      urls,
      withShort,
    }
    // 가격 정보 및 계산(sourceType, 목소리)
    const voice = voiceList?.find((el) => el.id === watch('voiceId'))
    const userVoice = userVoiceList?.find(
      (el) => el.id === watch('userVoiceId')
    )
    const subtitle = subTitleList?.find((el) => el.id === watch('subtitleId'))
    let voicePrice = 0
    if (voice) {
      voicePrice = voice.originPrice
      delete textVideoData.userVoiceId
    } else if (userVoice) {
      voicePrice = userVoice.originPrice
      delete textVideoData.voiceId
    }

    if (sourceTypeId === 4 || sourceTypeId === 5) {
      const [sourceTypeInfo] =
        sourceTypeList
          ?.filter((el) => el.id === 2)[0]
          .children?.filter((el) => el.id === sourceTypeId) ?? []
      setTextVideoPriceInfo({
        voicePrice: voicePrice,
        sourceTypePrice: Number(sourceTypeInfo.originPrice) ?? 0,
        subtitlePrice: Number(subtitle?.originPrice) ?? 0,
      })
    } else {
      setTextVideoPriceInfo({
        voicePrice: voicePrice,
        subtitlePrice: Number(subtitle?.originPrice) ?? 0,
      })
    }

    setTextVideoInfo(textVideoData)
    setUseModal(true)
  }

  useEffect(() => {
    const saveData: TextTypeVideoInfo = getLocalStorage('contentTextVideoData')
    if (saveData) {
      const {
        sourceType,
        sourceTypeId,
        languageId,
        voiceId,
        userVoiceId,
        subtitleColorId,
        subtitleId,
      } = saveData
      console.log('localstorage  exist!')
      if (sourceType) {
        setValue('sourceType', sourceType)
      }
      if (sourceTypeId) {
        setValue('sourceTypeId', sourceTypeId)
      }
      if (languageId && voiceId) {
        setValue('languageId', languageId)
        const voice = voiceList?.find((el) => (el.id = voiceId))
        if (voice) {
          setValue('voiceId', voice.id)
        }
      }
      if (languageId && userVoiceId) {
        setValue('languageId', languageId)
        const voice = userVoiceList?.find((el) => el.id === userVoiceId)

        if (voice) {
          setValue('userVoiceId', voice.id)
        }
      }
      if (subtitleId && subTitleList) {
        const subtitle = subTitleList?.find((el) => el.id === subtitleId)
        subtitle
          ? setValue('subtitleId', subtitle.id)
          : setDefaultValueFromListInTextvideo(
              subTitleList,
              setValue,
              'subtitleId'
            )
      } else {
        setDefaultValueFromListInTextvideo(subTitleList, setValue, 'subtitleId')
      }
      if (subtitleColorId && subtitleColorList) {
        const subtitleColor = subtitleColorList?.find(
          (el) => el.id === subtitleColorId
        )
        subtitleColor
          ? setValue('subtitleColorId', subtitleColor.id)
          : setDefaultValueFromListInTextvideo(
              subtitleColorList,
              setValue,
              'subtitleColorId'
            )
      } else {
        setDefaultValueFromListInTextvideo(
          subtitleColorList,
          setValue,
          'subtitleColorId'
        )
      }
    } else {
      console.log('localstorage not exist!')
      if (sourceTypeList) {
        setValue('sourceType', sourceTypeList[0]?.name)
        setValue('sourceTypeId', sourceTypeList[0]?.id)
      }
      if (languageList) {
        setValue('languageId', languageList[0].id)
      }

      if (subTitleList) {
        setDefaultValueFromListInTextvideo(subTitleList, setValue, 'subtitleId')
      }
      if (subtitleColorList) {
        setDefaultValueFromListInTextvideo(
          subtitleColorList,
          setValue,
          'subtitleColorId'
        )
      }
      if (voiceList) {
        setDefaultValueFromListInTextvideo(voiceList, setValue, 'voiceId')
      }
    }
  }, [
    sourceTypeList,
    languageList,
    subtitleColorList,
    subTitleList,
    userVoiceList,
  ])

  return (
    <>
      <div className="flex flex-col relative mt-5 w-full ">
        <div className="w-full absolute h-[102px] z-40 flex ">
          <button
            className={classNames(
              'w-[50%] h-full md:rounded-tl-[40px] flex items-start justify-center ',
              {
                ' bg-[#2E343D]  text-white': tab === 'link',
                ' bg-[#EEF1F7]   text-[#8F929B]': tab !== 'link',
              }
            )}
            type="button"
            onClick={() => {
              changeTab('link')
            }}
          >
            <span className="  text-[18px] font-bold  pt-5">
              {t('contentVideoEditor.textType.tabs.linkCreate')}
            </span>
          </button>
          <button
            className={classNames(
              'w-[50%] h-full md:rounded-tr-[40px] flex items-start justify-center ',
              {
                ' bg-[#2E343D]  text-white': tab === 'text',
                ' bg-[#EEF1F7]   text-[#8F929B]': tab !== 'text',
              }
            )}
            onClick={() => {
              changeTab('text')
            }}
            type="button"
          >
            <span className=" text-[18px] font-bold  pt-5">
              {t('contentVideoEditor.textType.tabs.textCreate')}
            </span>
          </button>
        </div>
        <form
          className={`flex flex-col gap-[20px] mt-[60px] z-50`}
          onSubmit={handleSubmit(onSubmit, (err) => {
            console.log(err)
          })}
        >
          <ShadowBox className="py-[40px]  md:w-[816px] bg-[#FFFFFF] flex flex-col gap-8">
            <div className="flex flex-col">
              <div className="flex flex-col gap-[16px]  w-full mb-4">
                {' '}
                <div className="flex flex-col w-[343px] md:w-full ml-4 md:ml-0">
                  <div className="relative w-[210px]">
                    <h3 className="font-bold text-[#2E343D] text-[20px] md:text-[24px] ">
                      {t('contentVideoEditor.textType.contentInput.title')}
                    </h3>
                    {/* <ContentTooltip
                    setContentTooltipStatus={setContentTooltipStatus}
                    contentTooltipStatus={contentTooltipStatus}
                  /> */}
                  </div>
                  <p className="text-main text-[14px]">
                    {t('contentVideoEditor.textType.contentInput.description')}
                  </p>
                  <p className="text-error text-[14px]">
                    {t('contentVideoEditor.textType.contentInput.warning')}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <div className="relative h-5 w-5 shrink-0">
                          <input
                            type="checkbox"
                            checked={userWritten}
                            onChange={(e) => {
                              const value: boolean = e.target.checked
                              setValue('userWritten', value ? 1 : 0)
                              value ? setMaxLength(400) : setMaxLength(1000)
                              setUserWritten(value)
                            }}
                            className="sr-only peer"
                          />

                          <span className="absolute inset-0 flex items-center justify-center rounded-[4px] bg-[#e4e7ec] peer-checked:bg-href peer-checked:before:text-xl before:text-white before:content-['✓']"></span>
                        </div>

                        <span className="text-[#8F929B]">
                          {t(
                            'contentVideoEditor.textType.contentInput.useOwnScript'
                          )}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative w-full flex justify-center flex-col items-center">
                <Controller
                  name={'idea'}
                  rules={{
                    required: {
                      value: true,
                      message: t(
                        'contentVideoEditor.textType.contentInput.required'
                      ),
                    },
                    maxLength: {
                      value: maxLength,
                      message: `소재내용의 길이가 ${maxLength}${t(
                        'contentVideoEditor.textType.contentInput.maxLengthError'
                      )}`,
                    },
                    validate: (value) => {
                      return PRODUCT_NAME_PATTERN.test(value)
                        ? true
                        : t(
                            'contentVideoEditor.textType.contentInput.invalidChars'
                          )
                    },
                  }}
                  render={({
                    field: { onBlur, value, ...field },
                    fieldState,
                  }) => {
                    return (
                      <>
                        {isMobile ? (
                          <TextArea
                            error={
                              value.length > maxLength ||
                              !PRODUCT_NAME_PATTERN.test(value)
                                ? true
                                : false
                            }
                            value={value}
                            {...field}
                            placeholder={
                              userWritten
                                ? t(
                                    'contentVideoEditor.textType.contentInput.placeholderOwn'
                                  )
                                : t(
                                    'contentVideoEditor.textType.contentInput.placeholderGenerate'
                                  )
                            }
                            maxLength={maxLength}
                          />
                        ) : (
                          <TextAreaContent
                            error={
                              value.length > maxLength ||
                              !PRODUCT_NAME_PATTERN.test(value)
                                ? true
                                : false
                            }
                            value={value}
                            {...field}
                            placeholder={
                              userWritten
                                ? t(
                                    'contentVideoEditor.textType.contentInput.placeholderOwn'
                                  )
                                : t(
                                    'contentVideoEditor.textType.contentInput.placeholderGenerate'
                                  )
                            }
                            maxLength={maxLength}
                          />
                        )}
                        <span
                          className={classNames(
                            ' absolute md:right-3 right-5 md:bottom-9 bottom-9 z-50 text-[#8F929B] text-[14px]',
                            {
                              'text-error font-bold':
                                watch('idea').length > maxLength,
                            }
                          )}
                        >
                          {`(${watch('idea').length}/${maxLength})`}
                        </span>

                        <div
                          className={`flex gap-[4px] mt-[8px] justify-start w-full ${
                            (value.length > maxLength ||
                              !PRODUCT_NAME_PATTERN.test(value)) &&
                            'text-error'
                          }`}
                        >
                          <span className="md:text-base text-[14px]">
                            {t(
                              'contentVideoEditor.textType.contentInput.invalidCharsDescription'
                            )}
                          </span>
                        </div>
                      </>
                    )
                  }}
                  control={control}
                />
              </div>
            </div>
            <div className="flex flex-col items-center w-full md:w-[736px]  gap-[8px]">
              <div className="flex flex-col gap-[16px]">
                <div className=" flex flex-col  md:ml-0">
                  <div className="relative flex items-center gap-2">
                    {' '}
                    <h3 className="font-bold text-[#2E343D] md:text-[24px] text-[20px] ">
                      {t('contentVideoEditor.textType.titleInput.title')}
                    </h3>
                    {/* <ShortVideoTitleTooltip
                      setTitleTooltip={setTitleTooltip}
                      titleTooltip={titleTooltip}
                    /> */}
                  </div>

                  <p className="text-main text-[14px]">
                    {t('contentVideoEditor.textType.titleInput.description')}
                  </p>
                </div>
                <div className="flex flex-col items-center w-full md:w-[736px] gap-[8px]">
                  <div className="relative">
                    <Controller
                      render={({ field }) => (
                        <>
                          {isMobile ? (
                            <TextArea
                              userCheck={userCheck}
                              error={
                                field.value.length > 50 ||
                                !PRODUCT_NAME_PATTERN.test(field.value)
                                  ? true
                                  : false
                              }
                              {...field}
                              placeholder={t(
                                'contentVideoEditor.textType.titleInput.placeholder'
                              )}
                              maxLength={50}
                            />
                          ) : (
                            <Input
                              userCheck={userCheck}
                              error={
                                field.value.length > 50 ||
                                !PRODUCT_NAME_PATTERN.test(field.value)
                                  ? true
                                  : false
                              }
                              {...field}
                              placeholder={t(
                                'contentVideoEditor.textType.titleInput.placeholder'
                              )}
                              maxLength={50}
                            />
                          )}
                          <span className=" absolute right-3 md:bottom-[54%] bottom-10 z-50 text-[#8F929B] text-[14px] ">
                            {`(${watch('title').length}/50)`}
                          </span>
                          <div
                            className={`flex gap-[4px] mt-[8px] ${
                              (field.value.length > 50 ||
                                !PRODUCT_NAME_PATTERN.test(field.value)) &&
                              'text-error'
                            }`}
                          >
                            <span className="md:text-base text-[14px]">
                              {t(
                                'contentVideoEditor.textType.contentInput.invalidCharsDescription'
                              )}
                            </span>
                          </div>
                        </>
                      )}
                      name={'title'}
                      rules={{
                        required: {
                          value: true,
                          message: t(
                            'contentVideoEditor.textType.titleInput.required'
                          ),
                        },
                        maxLength: {
                          value: 90,
                          message: t(
                            'contentVideoEditor.textType.titleInput.maxLengthError'
                          ),
                        },
                        validate:
                          watch(`title`) !== ''
                            ? (value) => {
                                return (
                                  PRODUCT_NAME_PATTERN.test(value) ||
                                  t(
                                    'contentVideoEditor.textType.contentInput.invalidChars'
                                  )
                                )
                              }
                            : undefined,
                      }}
                      control={control}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* 영상 소스 */}
            <div className="w-full flex flex-col gap-4 md:px-0 px-4">
              <p className="text-[18px] font-bold">
                {t('contentVideoEditor.textType.videoSource.title')}
              </p>
              <div className="grid md:grid-cols-3 grid-cols-1 md:h-[60px] h-[240px] gap-x-1">
                {sourceTypeList?.map((sourceType, index) => (
                  <>
                    <button
                      className={classNames(
                        'border-[1px] border-[#D9DEE8] px-6 rounded-[6px] text-base font-bold  md:h-auto h-[60px]',
                        {
                          'text-[#B6BBC6] ':
                            watch('sourceType') !== sourceType.name,
                          'text-white bg-main ':
                            watch('sourceType') === sourceType.name,
                        }
                      )}
                      type="button"
                      onClick={() => {
                        setValue('sourceType', sourceType?.name)
                        setValue('sourceTypeId', sourceType?.id)
                        setSelectedSourceType(sourceType)
                        setSelectedSourceTypeChildrenId(sourceType?.id)
                        const children = sourceType.children ?? []
                        if (children.length > 0) {
                          setSelectedSourceTypeChildrenId(children[0]?.id)
                          setValue('sourceTypeId', children[0]?.id)
                        }
                      }}
                    >
                      <CircularCheckbox
                        id={index}
                        onChange={() => {}}
                        label={sourceType.name}
                        checked={watch('sourceType') === sourceType.name}
                      />
                      {index === 0 && (
                        <img
                          src="assets/img/stock.png"
                          alt=""
                          className="ml-7 w-[133px] h-[18px]"
                        />
                      )}
                    </button>
                  </>
                ))}
              </div>
              {watch('sourceType') === 'AI 생성 이미지' && (
                <>
                  <div className="w-full md:h-[62px] h-[80px] bg-[#F6F8FB] rounded-[10px] grid grid-cols-2 px-5 md:py-0 py-4">
                    {sourceTypeList
                      ?.filter((el) => el.name === watch('sourceType'))[0]
                      ?.children?.map((el, index) => (
                        <>
                          <button
                            className="md:flex gap-1 md:items-center flex-col md:flex-row"
                            type="button"
                            onClick={() => {
                              setSelectedSourceTypeChildrenId(el.id)
                              setValue('sourceTypeId', el.id)
                            }}
                          >
                            {' '}
                            <CircularCheckbox
                              id={index}
                              onChange={() => {
                                setValue('sourceTypeId', el.id)
                              }}
                              label={el.name}
                              checked={watch('sourceTypeId') === el.id}
                            />
                            <s className="text-[14px] text-[#5E6068]">
                              {' '}
                              {Number(el.price).toLocaleString()}
                            </s>
                            {` `}
                            <span className="text-[14px] text-main font-bold ">
                              {el.originPrice}
                              {t(
                                'contentVideoEditor.textType.pricing.currentPrice'
                              )}
                            </span>
                          </button>
                        </>
                      ))}
                  </div>
                </>
              )}
              {watch('sourceType') === '이미지 업로드' && (
                <>
                  {' '}
                  <div
                    style={{
                      background: '#F6F8FB',
                      borderRadius: '10px',
                      padding: '20px',
                    }}
                    className="custom-scroll overflow-y-scroll max-h-[460px]"
                  >
                    <div className="text-[16px]  mb-1">
                      <span className="font-bold">
                        {t('contentVideoEditor.textType.imageUpload.title')}{' '}
                      </span>{' '}
                      <span className="text-[#8F929B] text-[16px] text-sm">
                        {t('contentVideoEditor.textType.imageUpload.fileInfo')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mb-3"></div>
                    <div
                      className={classNames('mb-5', {
                        'flex gap-1 overflow-x-auto ': isMobile,
                        'grid grid-cols-8 gap-1   ': !isMobile,
                      })}
                    >
                      {/* local image upload 슬롯 */}
                      {uploadImages.map((el, index) => (
                        <div
                          key={index}
                          className={classNames('bg-black shrink-0 relative', {
                            'w-[85px] h-[151px] relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center':
                              isMobile,
                            'w-[85px] h-[151px] relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center ':
                              !isMobile,
                          })}
                        >
                          {el.uploading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-10">
                              <svg
                                className="w-6 h-6 text-white animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                />
                              </svg>
                            </div>
                          )}
                          <img
                            src={el.preview}
                            alt={`img_${index + 1}`}
                            style={styles.image}
                          />
                          <div className="absolute flex items-center cursor-pointer right-1 top-1 z-50 active:scale-95">
                            <div
                              className={
                                'w-[28px] h-[28px] rounded-full border-[1px] bg-[#2E343D] flex items-center justify-center '
                              }
                            >
                              <button
                                className="w-[24px] h-[24px] bg-[#2E343D] rounded-full text-white flex items-center justify-center font-bold "
                                type="button"
                                onClick={() => {
                                  handleDeleteFile(el.file, el.preview)
                                }}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </div>
                          <div className="absolute flex items-center cursor-pointer right-1 bottom-1 z-50 active:scale-95">
                            <div
                              className={
                                'w-[28px] h-[28px] rounded-full border-2 bg-white flex items-center justify-center  border-href'
                              }
                            >
                              <div className="w-[22px] h-[22px] bg-href rounded-full text-white flex items-center justify-center font-bold">
                                {index + 1}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* local image 빈 슬롯 */}
                      {new Array(4 - (uploadImages.length ?? 0))
                        .fill(1)
                        .map((src, index) => (
                          <div key={index} className="cursor-pointer">
                            <div className=" relative">
                              <LocalImageUpload
                                handleButtonClick={handleButtonClick}
                                index={index}
                                isPending={isPending}
                              />
                              <input
                                id="file-input"
                                type="file"
                                accept=".jpg,.jpeg,.png"
                                className="hidden"
                                onChange={handleFileChange}
                              />
                            </div>
                          </div>
                        ))}
                    </div>

                    {/* ㄴㄴ */}
                  </div>
                </>
              )}
            </div>
            <VideoRatioType />
            {/* <Upload
              watch={watch}
              setValue={setValue}
              control={control}
              userInfo={userInfo}
            /> */}
          </ShadowBox>

          <>
            <ShadowBox
              className={classNames('py-[40px] px-[16px] md:w-[816px]', {
                'opacity-30 pointer-events-none': '',
              })}
            >
              <div className="flex flex-col gap-[40px] md:gap-0 w-[343px] md:w-[736px]">
                <div className="flex flex-col items-center  mt-[16px] md:mb-[40px] relative ">
                  <p className="font-bold text-[#2E343D] text-[24px] ">
                    {t('contentVideoEditor.textType.effects.title')}
                  </p>
                  <p className="text-main text-[14px]">
                    {t('contentVideoEditor.textType.effects.description')}
                  </p>
                  {/* <div className="md:absolute  md:right-0 md:mt-0 mt-4 flex flex-col items-center">
                    <p className=" bg-clip-text text-transparent bg-gradient-to-r from-[rgba(96,149,255,1)] to-[rgba(213,18,231,1)]">
                      지금 놓치면 끝!
                    </p>
                    <p className="text-[#FD6767]">🔥 6/30 까지만</p>
                    <img
                      src="assets/img/short_video_editor/sale.png"
                      alt="sale"
                      width={141}
                      height={32}
                    />
                  </div> */}
                </div>

                <TextTypeAiScriptDisplay
                  watch={watch}
                  setValue={setValue}
                  control={control}
                  languageList={languageList}
                  userVoiceList={userVoiceList}
                  userVoiceRefetch={userVoiceRefetch}
                  voiceList={voiceList}
                  voiceRefetch={voiceRefetch}
                  userInfo={userInfo}
                  voiceTags={voiceTags}
                />
                <SubtitleDisplay
                  subTitleList={subTitleList ?? []}
                  watch={watch}
                  control={control}
                  setValue={setValue}
                  userInfo={userInfo}
                  setSubtitleTooltip={setSubtitleTooltip}
                  subtitleTooltip={subtitleTooltip}
                  subtitleColorList={subtitleColorList ?? []}
                />
              </div>
            </ShadowBox>
          </>
          <div className="flex justify-center">
            <p className="text-center">
              {t('contentVideoEditor.textType.disclaimer.part1')}
              <br />
              {t('contentVideoEditor.textType.disclaimer.part2')}
              <br />
              {t('contentVideoEditor.textType.disclaimer.part3')}
            </p>
          </div>
          <div className="w-full">
            <button
              className="w-full bg-main text-[18px] rounded-full text-white py-5 font-bold"
              type="submit"
            >
              {t('contentVideoEditor.textType.buttons.generateRequest')}
            </button>
          </div>
        </form>
      </div>
      {isUseModal && (
        <TextCashUseModal
          isUseModal={isUseModal}
          setUseModal={setUseModal}
          currentPoint={myPointData.totalPoint}
          textVideoInfo={textVideoInfo}
          textVideoPriceInfo={textVideoPriceInfo}
          setSaveSuccessModal={setSaveSuccessModal}
          setValue={setValue}
          getMyPointRefetch={getMyPointRefetch}
          setUploadImages={setUploadImages}
        />
      )}
      {saveSuccessModal && (
        <Modal isModal={saveSuccessModal}>
          <div className="bg-white w-[335px] h-[184px] p-[20px] border-t rounded-t-[20px]">
            <div className="flex flex-col items-center justify-center h-full gap-[24px]">
              <p className="font-bold text-[18px]">
                {t('contentVideoEditor.textType.modals.requestComplete.title')}
              </p>
              <div>
                <p className="text-[14px] font-bold">
                  {t(
                    'contentVideoEditor.textType.modals.requestComplete.description1'
                  )}
                </p>
                <p className="text-[14px] font-bold">
                  {t(
                    'contentVideoEditor.textType.modals.requestComplete.description2'
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="flex  w-[335px]">
            <div className="w-[50%] bg-[#EEF1F7] h-[60px] border-b border-[#EEF1F7] rounded-bl-[20px] flex justify-center items-center">
              <button
                className="text-[#5E6068] w-full font-bold"
                onClick={() => {
                  navigate('/my-videos?type=tcv&page=1&pageSize=80')
                }}
              >
                {t('contentVideoEditor.textType.buttons.goToUsageHistory')}
              </button>
            </div>
            <div className="w-[50%] bg-main h-[60px] border-b border-main rounded-br-[20px] flex justify-center items-center">
              <button
                className="text-white w-full font-bold"
                onClick={(event) => {
                  setSaveSuccessModal(false)
                }}
              >
                {t('contentVideoEditor.textType.buttons.confirm')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {isErrorModal && (
        <ErrorModal
          isErrorModal={isErrorModal}
          setIsErrorModal={setIsErrorModal}
          errorTitle={errorTitle}
          errorSubtitle={errorSubtitle}
        />
      )}

      {isSceneErrorModal && (
        <SceneErrorModal
          isSceneErrorModal={isSceneErrorModal}
          setIsSceneErrorModal={setIsSceneErrorModal}
        />
      )}
    </>
  )
}
const SceneErrorModal = (props: any) => {
  const { isSceneErrorModal, setIsSceneErrorModal } = props
  const { t } = useTranslation()
  return (
    <>
      <Modal isModal={isSceneErrorModal as boolean}>
        <div className="bg-white w-[335px] h-[184px] p-[20px] border-t rounded-t-[20px] a">
          <div className="flex flex-col items-center justify-center h-full gap-[24px]">
            <p className="font-bold text-[18px]">
              {t('contentVideoEditor.textType.modals.sceneError.title')}
            </p>
            <div>
              <p className="text-[14px] ">
                {t('contentVideoEditor.textType.modals.sceneError.description')}
              </p>
            </div>
          </div>
        </div>
        <div className="flex  w-[335px]">
          <div className="w-[100%] bg-main h-[60px] border-b border-main rounded-b-[20px] flex justify-center items-center">
            <button
              className="text-white w-full font-bold"
              onClick={(event) => {
                setIsSceneErrorModal(false)
              }}
            >
              {t('contentVideoEditor.textType.buttons.confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
const ErrorModal = (props: any) => {
  const { isErrorModal, setIsErrorModal, errorTitle, errorSubtitle } = props
  const { t } = useTranslation()
  return (
    <>
      <Modal isModal={isErrorModal as boolean}>
        <div className="bg-white w-[335px] h-[184px] p-[20px] border-t rounded-t-[20px]">
          <div className="flex flex-col items-center justify-center h-full gap-[24px]">
            {errorTitle}
            <div>{errorSubtitle}</div>
          </div>
        </div>
        <div className="flex  w-[335px]">
          <div className="w-[100%] bg-main h-[60px] border-b border-main rounded-b-[20px] flex justify-center items-center">
            <button
              className="text-white w-full font-bold"
              onClick={(event) => {
                setIsErrorModal(false)
              }}
            >
              {t('contentVideoEditor.textType.buttons.confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
